import React, { useState, useEffect, useRef, useCallback } from "react";
import { get, post } from "../../services/smartApiService";
import { LOGIN_URLS } from "../../api/LoginUrls";
import "./TelegramDashboard.scss";

interface TelegramStatus {
  configured: boolean;
  connected: boolean;
  phone_configured: boolean;
  already_authorized?: boolean;
  user?: {
    id: number;
    name: string;
    username: string;
    phone: string;
  };
  error?: string;
}

interface Dialog {
  id: number;
  name: string;
  type: string;
  unread_count: number;
}

interface Message {
  id: number;
  date: string;
  text: string;
  has_media: boolean;
  views: number | null;
  forwards: number | null;
  sender?: {
    id: number;
    name: string;
    username: string | null;
  };
}

interface ChannelInfo {
  id: number;
  name: string;
  username: string | null;
}

type AuthStep = "phone" | "code" | "password" | "connected";

interface TelegramPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadUpdate?: (count: number) => void;
}

const TelegramPanel: React.FC<TelegramPanelProps> = ({ isOpen, onClose, onUnreadUpdate }) => {
  const [status, setStatus] = useState<TelegramStatus | null>(null);
  const [dialogs, setDialogs] = useState<Dialog[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<ChannelInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchUsername, setSearchUsername] = useState<string>("");

  // Auth state
  const [authStep, setAuthStep] = useState<AuthStep>("phone");
  const [phone, setPhone] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [phoneCodeHash, setPhoneCodeHash] = useState<string>("");
  const [authLoading, setAuthLoading] = useState<boolean>(false);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const autoRefreshRef = useRef<NodeJS.Timeout | null>(null);
  const AUTO_REFRESH_INTERVAL = 60000; // 1 minute

  // Load status on open
  useEffect(() => {
    if (isOpen) {
      loadTelegramStatus();
    }
    return () => {
      stopAutoRefresh();
    };
  }, [isOpen]);

  // Auto-refresh messages
  useEffect(() => {
    if (autoRefresh && selectedChannel && status?.connected) {
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }
    return () => stopAutoRefresh();
  }, [autoRefresh, selectedChannel, status?.connected]);

  const startAutoRefresh = () => {
    stopAutoRefresh();
    autoRefreshRef.current = setInterval(() => {
      if (selectedChannel) {
        refreshMessages(selectedChannel.id);
      }
    }, AUTO_REFRESH_INTERVAL);
  };

  const stopAutoRefresh = () => {
    if (autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current);
      autoRefreshRef.current = null;
    }
  };

  const refreshMessages = (channelId: number) => {
    get(`${LOGIN_URLS.TELEGRAM_MESSAGES}/${channelId}?limit=50`, { showLoading: false }).subscribe({
      next: (response: any) => {
        setMessages(response.data.messages || []);
        setSelectedChannel(response.data.channel);
      },
      error: () => {},
    });
  };

  // Notify parent of unread count changes
  const syncUnreadCount = useCallback((dialogList: Dialog[]) => {
    if (onUnreadUpdate) {
      const total = dialogList.reduce((sum, d) => sum + (d.unread_count || 0), 0);
      onUnreadUpdate(total);
    }
  }, [onUnreadUpdate]);

  // Mark channel as read on Telegram servers
  const markAsRead = useCallback((channelId: number) => {
    post(`${LOGIN_URLS.TELEGRAM_MARK_READ}/${channelId}`, {}, { showLoading: false }).subscribe({
      next: () => {
        // Update local dialog unread count
        setDialogs((prev) => {
          const updated = prev.map((d) =>
            d.id === channelId ? { ...d, unread_count: 0 } : d
          );
          syncUnreadCount(updated);
          return updated;
        });
      },
      error: () => {},
    });
  }, [syncUnreadCount]);

  const loadTelegramStatus = () => {
    setLoading(true);
    setError(null);

    get(LOGIN_URLS.TELEGRAM_STATUS, { showLoading: false }).subscribe({
      next: (response: any) => {
        setStatus(response.data);
        if (response.data.connected) {
          setAuthStep("connected");
          loadDialogs();
        } else {
          setAuthStep("phone");
          setLoading(false);
        }
      },
      error: () => {
        setError("Failed to get Telegram status");
        setLoading(false);
      },
    });
  };

  const loadDialogs = () => {
    get(LOGIN_URLS.TELEGRAM_DIALOGS, { showLoading: false }).subscribe({
      next: (response: any) => {
        const dialogList = response.data.dialogs || [];
        setDialogs(dialogList);
        syncUnreadCount(dialogList);
        setLoading(false);
      },
      error: () => {
        setError("Failed to load dialogs");
        setLoading(false);
      },
    });
  };

  const handleSendCode = () => {
    if (!phone.trim()) {
      setError("Please enter your phone number");
      return;
    }

    setAuthLoading(true);
    setError(null);

    post(LOGIN_URLS.TELEGRAM_SEND_CODE, { phone: phone.trim() }).subscribe({
      next: (response: any) => {
        setAuthLoading(false);
        if (response.data.already_authorized) {
          setSuccessMessage("Already logged in!");
          loadTelegramStatus();
        } else if (response.data.success) {
          setPhoneCodeHash(response.data.phone_code_hash);
          setAuthStep("code");
          setSuccessMessage("Code sent! Check your Telegram app.");
        }
      },
      error: (err: any) => {
        setAuthLoading(false);
        setError(err.response?.data?.detail || "Failed to send code");
      },
    });
  };

  const handleVerifyCode = () => {
    if (!code.trim()) {
      setError("Please enter the verification code");
      return;
    }

    setAuthLoading(true);
    setError(null);

    post(LOGIN_URLS.TELEGRAM_VERIFY_CODE, {
      phone: phone.trim(),
      code: code.trim(),
      phone_code_hash: phoneCodeHash,
    }).subscribe({
      next: (response: any) => {
        setAuthLoading(false);
        if (response.data.requires_2fa) {
          setAuthStep("password");
          setSuccessMessage("2FA required. Enter your password.");
        } else if (response.data.success) {
          setSuccessMessage("Connected to Telegram!");
          setAuthStep("connected");
          loadTelegramStatus();
        }
      },
      error: (err: any) => {
        setAuthLoading(false);
        setError(err.response?.data?.detail || "Invalid code");
      },
    });
  };

  const handleVerifyPassword = () => {
    if (!password.trim()) {
      setError("Please enter your 2FA password");
      return;
    }

    setAuthLoading(true);
    setError(null);

    post(LOGIN_URLS.TELEGRAM_VERIFY_PASSWORD, { password: password.trim() }).subscribe({
      next: (response: any) => {
        setAuthLoading(false);
        if (response.data.success) {
          setSuccessMessage("Connected to Telegram!");
          setAuthStep("connected");
          loadTelegramStatus();
        }
      },
      error: (err: any) => {
        setAuthLoading(false);
        setError(err.response?.data?.detail || "Invalid password");
      },
    });
  };

  const handleLogout = () => {
    setAuthLoading(true);
    setError(null);

    post(LOGIN_URLS.TELEGRAM_LOGOUT, {}).subscribe({
      next: () => {
        setAuthLoading(false);
        setSuccessMessage("Logged out from Telegram");
        setStatus(null);
        setDialogs([]);
        setMessages([]);
        setSelectedChannel(null);
        setPhone("");
        setCode("");
        setPassword("");
        setPhoneCodeHash("");
        setAuthStep("phone");
        setAutoRefresh(false);
        if (onUnreadUpdate) onUnreadUpdate(0);
        loadTelegramStatus();
      },
      error: (err: any) => {
        setAuthLoading(false);
        setError(err.response?.data?.detail || "Failed to logout");
      },
    });
  };

  const handleForceReset = () => {
    setAuthLoading(true);
    setError(null);

    post(LOGIN_URLS.TELEGRAM_RESET_SESSION, {}).subscribe({
      next: (response: any) => {
        setAuthLoading(false);
        setSuccessMessage(response.data?.message || "Session cleared. Please re-authenticate.");
        setStatus(null);
        setDialogs([]);
        setMessages([]);
        setSelectedChannel(null);
        setPhone("");
        setCode("");
        setPassword("");
        setPhoneCodeHash("");
        setAuthStep("phone");
        setAutoRefresh(false);
        if (onUnreadUpdate) onUnreadUpdate(0);
        loadTelegramStatus();
      },
      error: (err: any) => {
        setAuthLoading(false);
        setError(err.response?.data?.detail || "Failed to reset session");
      },
    });
  };

  const loadMessages = (channelId: number) => {
    setMessagesLoading(true);
    setMessages([]);

    get(`${LOGIN_URLS.TELEGRAM_MESSAGES}/${channelId}?limit=50`, { showLoading: false }).subscribe({
      next: (response: any) => {
        setMessages(response.data.messages || []);
        setSelectedChannel(response.data.channel);
        setMessagesLoading(false);
        // Mark as read on Telegram after loading messages
        markAsRead(channelId);
      },
      error: () => {
        setError("Failed to load messages");
        setMessagesLoading(false);
      },
    });
  };

  const loadMessagesByUsername = () => {
    if (!searchUsername.trim()) return;

    setMessagesLoading(true);
    setMessages([]);

    const username = searchUsername.replace("@", "").trim();
    get(`${LOGIN_URLS.TELEGRAM_MESSAGES_USERNAME}/${username}?limit=50`, { showLoading: false }).subscribe({
      next: (response: any) => {
        setMessages(response.data.messages || []);
        setSelectedChannel(response.data.channel);
        setMessagesLoading(false);
        // Mark as read
        if (response.data.channel?.id) {
          markAsRead(response.data.channel.id);
        }
      },
      error: () => {
        setError(`Failed to load messages from @${username}`);
        setMessagesLoading(false);
      },
    });
  };

  const handleChannelClick = (dialog: Dialog) => {
    loadMessages(dialog.id);
  };

  const handleBackToChannels = () => {
    setSelectedChannel(null);
    setMessages([]);
    setAutoRefresh(false);
    // Refresh dialogs to get updated unread counts
    loadDialogs();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" }) +
      " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "channel": return "fa-bullhorn";
      case "group": return "fa-users";
      case "user": return "fa-user";
      default: return "fa-comment";
    }
  };

  const renderAuthForm = () => {
    if (!status?.configured) {
      return (
        <div className="tg-panel-auth">
          <div className="tg-auth-notice">
            <i className="fa fa-exclamation-triangle"></i>
            <p><strong>Telegram API not configured.</strong></p>
            <p>Set <code>TELEGRAM_API_ID</code> and <code>TELEGRAM_API_HASH</code> in backend .env</p>
          </div>
        </div>
      );
    }

    return (
      <div className="tg-panel-auth">
        <div className="tg-auth-header">
          <i className="fab fa-telegram"></i>
          <span>Connect to Telegram</span>
        </div>

        {/* Step indicators */}
        <div className="tg-steps">
          <div className={`tg-step ${authStep === "phone" ? "active" : ""} ${["code", "password", "connected"].includes(authStep) ? "done" : ""}`}>
            <span className="tg-step-dot">1</span>
            <span className="tg-step-label">Phone</span>
          </div>
          <div className="tg-step-line" />
          <div className={`tg-step ${authStep === "code" ? "active" : ""} ${["password", "connected"].includes(authStep) ? "done" : ""}`}>
            <span className="tg-step-dot">2</span>
            <span className="tg-step-label">Verify</span>
          </div>
          <div className="tg-step-line" />
          <div className={`tg-step ${authStep === "password" ? "active" : ""} ${authStep === "connected" ? "done" : ""}`}>
            <span className="tg-step-dot">3</span>
            <span className="tg-step-label">2FA</span>
          </div>
        </div>

        {error && (
          <div className="tg-error">
            <i className="fa fa-times-circle"></i> {error}
            <button className="tg-dismiss" onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {successMessage && (
          <div className="tg-success">
            <i className="fa fa-check-circle"></i> {successMessage}
            <button className="tg-dismiss" onClick={() => setSuccessMessage(null)}>&times;</button>
          </div>
        )}

        {authStep === "phone" && (
          <div className="tg-auth-field">
            <label>Phone Number</label>
            <div className="tg-input-group">
              <i className="fa fa-phone"></i>
              <input
                type="tel"
                placeholder="+91 98765 43210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSendCode()}
                disabled={authLoading}
              />
            </div>
            <button
              className="tg-btn tg-btn-primary"
              onClick={handleSendCode}
              disabled={authLoading || !phone.trim()}
            >
              {authLoading ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-paper-plane"></i>}
              <span>Send Code</span>
            </button>
          </div>
        )}

        {authStep === "code" && (
          <div className="tg-auth-field">
            <label>Verification Code</label>
            <div className="tg-input-group">
              <i className="fa fa-key"></i>
              <input
                type="text"
                placeholder="12345"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleVerifyCode()}
                disabled={authLoading}
                maxLength={6}
              />
            </div>
            <div className="tg-btn-row">
              <button className="tg-btn tg-btn-light" onClick={() => setAuthStep("phone")} disabled={authLoading}>
                <i className="fa fa-arrow-left"></i>
              </button>
              <button
                className="tg-btn tg-btn-primary tg-btn-flex"
                onClick={handleVerifyCode}
                disabled={authLoading || !code.trim()}
              >
                {authLoading ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-check"></i>}
                <span>Verify</span>
              </button>
            </div>
          </div>
        )}

        {authStep === "password" && (
          <div className="tg-auth-field">
            <label>Two-Factor Password</label>
            <div className="tg-input-group">
              <i className="fa fa-lock"></i>
              <input
                type="password"
                placeholder="Your 2FA password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleVerifyPassword()}
                disabled={authLoading}
              />
            </div>
            <button
              className="tg-btn tg-btn-primary"
              onClick={handleVerifyPassword}
              disabled={authLoading || !password.trim()}
            >
              {authLoading ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-unlock"></i>}
              <span>Submit</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderChannelsList = () => (
    <div className="tg-channels">
      <div className="tg-channels-header">
        <span className="tg-section-title">
          <i className="fa fa-list"></i> Channels & Chats
        </span>
        <div className="tg-header-actions">
          <button className="tg-icon-btn" onClick={loadDialogs} title="Refresh">
            <i className="fa fa-refresh"></i>
          </button>
          <button
            className="tg-icon-btn"
            onClick={handleForceReset}
            disabled={authLoading}
            title="Reset revoked/conflicted session (re-auth required)"
            style={{ color: "#f39c12" }}
          >
            <i className="fa fa-exclamation-triangle"></i>
          </button>
          <button
            className="tg-icon-btn tg-btn-danger"
            onClick={handleLogout}
            disabled={authLoading}
            title="Disconnect"
          >
            <i className="fa fa-sign-out"></i>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="tg-search">
        <input
          type="text"
          placeholder="Search @username..."
          value={searchUsername}
          onChange={(e) => setSearchUsername(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && loadMessagesByUsername()}
        />
        <button onClick={loadMessagesByUsername} disabled={!searchUsername.trim()}>
          <i className="fa fa-search"></i>
        </button>
      </div>

      <div className="tg-dialogs-list">
        {dialogs.length === 0 ? (
          <p className="tg-empty">No dialogs found</p>
        ) : (
          dialogs.map((dialog) => (
            <div
              key={dialog.id}
              className="tg-dialog-item"
              onClick={() => handleChannelClick(dialog)}
            >
              <span className="tg-dialog-icon">
                <i className={`fa ${getTypeIcon(dialog.type)}`}></i>
              </span>
              <div className="tg-dialog-info">
                <span className="tg-dialog-name">{dialog.name}</span>
                <span className="tg-dialog-type">{dialog.type}</span>
              </div>
              {dialog.unread_count > 0 && (
                <span className="tg-unread">{dialog.unread_count}</span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderMessages = () => (
    <div className="tg-messages">
      <div className="tg-messages-header">
        <button className="tg-icon-btn" onClick={handleBackToChannels}>
          <i className="fa fa-arrow-left"></i>
        </button>
        <div className="tg-channel-info">
          <span className="tg-channel-name">{selectedChannel?.name}</span>
          {selectedChannel?.username && (
            <span className="tg-channel-username">@{selectedChannel.username}</span>
          )}
        </div>
        <div className="tg-messages-actions">
          <button
            className={`tg-icon-btn ${autoRefresh ? "tg-active" : ""}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? "Auto-refresh ON (1 min)" : "Auto-refresh OFF"}
          >
            <i className={`fa ${autoRefresh ? "fa-toggle-on" : "fa-toggle-off"}`}></i>
          </button>
          <button className="tg-icon-btn" onClick={() => selectedChannel && refreshMessages(selectedChannel.id)} title="Refresh">
            <i className="fa fa-refresh"></i>
          </button>
        </div>
      </div>

      {autoRefresh && (
        <div className="tg-auto-indicator">
          <i className="fa fa-clock-o"></i> Auto-refreshing every 1 min
        </div>
      )}

      {messagesLoading ? (
        <div className="tg-loading">
          <i className="fa fa-spinner fa-spin"></i>
          <span>Loading messages...</span>
        </div>
      ) : (
        <div className="tg-messages-list">
          {messages.length === 0 ? (
            <p className="tg-empty">No messages found</p>
          ) : (
            messages.map((message) => (
              <div key={message.id} className="tg-message-item">
                <div className="tg-msg-top">
                  {message.sender && (
                    <span className="tg-sender">{message.sender.name}</span>
                  )}
                  <span className="tg-msg-time">{formatDate(message.date)}</span>
                </div>
                <div className="tg-msg-text">
                  {message.text || (
                    <span className="tg-media">
                      {message.has_media ? "[Media]" : "[Empty]"}
                    </span>
                  )}
                </div>
                {(message.views !== null || message.forwards !== null) && (
                  <div className="tg-msg-stats">
                    {message.views !== null && (
                      <span><i className="fa fa-eye"></i> {message.views}</span>
                    )}
                    {message.forwards !== null && (
                      <span><i className="fa fa-share"></i> {message.forwards}</span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="tg-panel-overlay" onClick={onClose} />
      <div className={`tg-panel ${isOpen ? "tg-panel-open" : ""}`}>
        {/* Panel Header */}
        <div className="tg-panel-header">
          <div className="tg-panel-title">
            <i className="fab fa-telegram"></i>
            <span>Telegram</span>
            {status?.connected && status.user && (
              <span className="tg-user-badge">{status.user.name}</span>
            )}
          </div>
          <button className="tg-close-btn" onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        {/* Panel Body */}
        <div className="tg-panel-body">
          {error && !loading && authStep === "connected" && (
            <div className="tg-error">
              <i className="fa fa-times-circle"></i> {error}
              <button className="tg-dismiss" onClick={() => setError(null)}>&times;</button>
            </div>
          )}

          {successMessage && authStep === "connected" && (
            <div className="tg-success">
              <i className="fa fa-check-circle"></i> {successMessage}
              <button className="tg-dismiss" onClick={() => setSuccessMessage(null)}>&times;</button>
            </div>
          )}

          {loading ? (
            <div className="tg-loading">
              <i className="fa fa-spinner fa-spin"></i>
              <span>Loading...</span>
            </div>
          ) : !status?.connected ? (
            renderAuthForm()
          ) : selectedChannel ? (
            renderMessages()
          ) : (
            renderChannelsList()
          )}
        </div>
      </div>
    </>
  );
};

export default TelegramPanel;
